import React from 'react';
import { InputAdornment, InputBase, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { withStyles } from '@mui/styles';
import Select from 'react-select';
import { filter, indexOf, map, range } from 'lodash';
import classnames from 'classnames';
import UIUtils from '../../../lib/UIUtils';
import { getLocalTimeZoneLabel } from 'js/lib/DateUtils';
import { cronToHumanReadable } from 'new/utils/cronDisplay';
import TimeSelector from '../TimeSelector';
import DateTimeField from '../DateTimeField';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  redesignStyles,
  SelectInput,
  SelectOption,
} from '../rselect/ReactSelectUtils';

import styles from './FrequencyFieldRedesign.module.scss';

const FREQUENCY_INTERVALS = [
  {
    label: 'Once',
    value: 'Once',
  },
  {
    label: 'Every 15 Minutes',
    value: '15 Minutes',
  },
  {
    label: 'Every 30 Minutes',
    value: '30 Minutes',
  },
  {
    label: 'Every Hour',
    value: 'Hour',
  },
  {
    label: 'Every N Hours',
    value: 'N Hours',
  },
  {
    label: 'Every Day',
    value: 'Day',
  },
  {
    label: 'Every N Days',
    value: 'N Days',
  },
  {
    label: 'Every Week',
    value: 'Week',
  },
  {
    label: 'Every Month',
    value: 'Month',
  },
  {
    label: 'Every N Months',
    value: 'N Months',
  },
];

const N_HOURS_OPTIONS = map(range(2, 24), (value) => ({
  label: value,
  value: String(value),
}));

const N_DAYS_OPTIONS = map(range(2, 31), (value) => ({
  label: value,
  value: String(value),
}));

const N_MONTHS_OPTIONS = map(range(2, 12), (value) => ({
  label: value,
  value: String(value),
}));

const StyledInputBase = withStyles((theme) => ({
  root: {
    'label + &': {
      marginBottom: 9,
    },
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme?.palette?.common?.white,
    borderRadius: 2,
    border: '1px solid #ccc',
  },
  input: {
    position: 'relative',
    fontWeight: 400,
    padding: 6,
    color: '#273438',
    height: 28,
    fontSize: 14,
  },
}))(InputBase);

const datePickerProps = {
  dropUp: true,
  className: [styles.widgetContainer],
  inputProps: { readOnly: true, disabled: true },
};

type FrequencyFieldRedesignProps = {
  name: string;
  label: string;
  value: string;
  onChange(...args: any): void;
};

class FrequencyFieldRedesign extends React.Component<FrequencyFieldRedesignProps> {
  constructor(props) {
    super(props);

    const currentTimeState = this._getStateFromCurrentTime('Day');

    this.state = {
      ...currentTimeState,
    };

    this._renderSelectField = this._renderSelectField.bind(this);
    this._handleInternalValueChanged = this._handleInternalValueChanged.bind(this);
    this._handleTimestampChanged = this._handleTimestampChanged.bind(this);
    this._updateValueFromState = this._updateValueFromState.bind(this);
    this._updateStateFromProps = this._updateStateFromProps.bind(this);
    this._getStateFromCurrentTime = this._getStateFromCurrentTime.bind(this);
  }

  componentDidMount() {
    if (
      this.props.value &&
      this.props.value !== '0 0/1 * 1/1 * ? *' /* if cron is "Continuous", fall back to default */
    ) {
      this._updateStateFromProps(this.props);
    } else {
      this._updateValueFromState(this.state);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.value !== this.props.value) {
      this._updateStateFromProps(this.props);
    }
  }

  render() {
    const { label, value, skinnyDisplay, labelClassName, limitOptions } = this.props as any;
    const { interval, hh, mm, aa, dayOfWeek, dayOfMonth, timestamp } = this.state as any;

    if (!interval) {
      return (
        <div>
          <label className={labelClassName}>{label} ( Cron Expression )</label>
          <StyledInputBase
            fullWidth
            margin="dense"
            inputProps={{ autoComplete: 'new-password' }}
            endAdornment={
              <InputAdornment position="end">
                <LockOutlined className={styles.cronLockIcon} />
              </InputAdornment>
            }
            disabled
            value={value}
          />
          <Typography variant="caption" className={styles.cronCaption}>
            {cronToHumanReadable(value) !== value && (
              <>
                <span className={styles.cronCaptionLabel}>Runs:</span>{' '}
                {cronToHumanReadable(value)}{' '}
              </>
            )}
            <span className={styles.cronCaptionMeta}>
              (edit via{' '}
              <a
                href="https://docs.nexla.com/reference/nexla-api#tag/Sources/operation/update_data_source"
                target="_blank"
                rel="noopener noreferrer"
              >
                Nexla API
              </a>
              )
            </span>
          </Typography>
        </div>
      );
    }

    const intervalNeedsN =
      interval === 'N Hours' || interval === 'N Days' || interval === 'N Months';
    const className = classnames('frequency-field', { skinny: skinnyDisplay });
    let intervals = FREQUENCY_INTERVALS;

    if (limitOptions !== null) {
      intervals = filter(intervals, (i) => indexOf(limitOptions, i.value) !== -1);
    }

    return (
      <div className={className}>
        <div className="frequency-types">
          <label className={classnames(labelClassName, styles.label)}>
            <Typography variant="body2" color="black">
              {label}
            </Typography>
          </label>
          {this._renderSelectField('interval', intervals, 180)}
          {intervalNeedsN && (
            <label className={styles.label}>
              <Typography variant="body2" color="black">
                Every
              </Typography>
            </label>
          )}
          {intervalNeedsN &&
            this._renderSelectField(
              'n',
              interval === 'N Hours'
                ? N_HOURS_OPTIONS
                : interval === 'N Days'
                  ? N_DAYS_OPTIONS
                  : N_MONTHS_OPTIONS,
              80,
            )}
          {intervalNeedsN && (
            <label className={styles.label}>
              <Typography variant="body2" color="black">
                {interval.slice(interval.lastIndexOf(' ') + 1)}
              </Typography>
            </label>
          )}
        </div>
        {(interval === 'Once' && (
          <div className="poll-time">
            <DateTimeField
              value={timestamp}
              onChange={this._handleTimestampChanged}
              datePickerProps={datePickerProps}
              customStyles={{
                container: { minWidth: 270, fontSize: 14 },
              }}
            />
            <label className={styles.label}>
              <Typography variant="body2" color="black">
                &nbsp;&nbsp;{getLocalTimeZoneLabel()}
              </Typography>
            </label>
          </div>
        )) || (
          <TimeSelector
            interval={interval}
            hh={hh}
            mm={mm}
            aa={aa}
            dayOfWeek={dayOfWeek}
            dayOfMonth={dayOfMonth}
            onChange={this._handleInternalValueChanged}
            customStyles={redesignStyles}
          />
        )}
      </div>
    );
  }

  _renderSelectField(name, options, width = 170) {
    const selectionValue = getCurrentSelectedOption(options, this.state[name]);
    const styles = {
      container: (base) => ({
        ...base,
        minWidth: width,
        marginRight: 8,
      }),
      control: (base) => ({
        ...base,
        fontWeight: 500,
        lineHeight: '1.5rem',
        color: 'var(--color-greyscale-600)',
        fontSize: '14px',
        borderRadius: '8px',
        border: '1px solid var(--color-greyscale-100)',
      }),
      valueContainer: (base) => ({ ...base, padding: '0  16px 0 12px' }),
      indicatorSeparator: (base) => ({ ...base, display: 'none' }),
      indicatorContainer: (base) => ({ ...base, padding: 0 }),
      singleValue: (base) => ({
        ...base,
        fontWeight: 500,
        lineHeight: '1.5rem',
        color: 'var(--color-greyscale-600)',
        fontSize: '14px',
      }),
      option: (styles, { isDisabled, isFocused, isSelected }) => {
        return {
          ...styles,
          fontSize: '14px',
          backgroundColor: isSelected ? '#2684FF' : isFocused ? '#B2D4FF' : '#fff',
          color: isSelected ? '#fff' : isDisabled ? ' #cccccc' : 'var(--color-greyscale-600)',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        };
      },
      menu: (base) => ({
        ...base,
        zIndex: 99999,
        fontWeight: 500,
        lineHeight: '1.5rem',
        color: 'var(--color-greyscale-600)',
        fontSize: '14px',
      }),
    };

    return (
      <Select
        menuPlacement="auto"
        value={selectionValue}
        className={`field-frequency-select __TEST_frequency-field-${this.props.name?.replaceAll('.', '-')}-${name}`}
        styles={styles}
        options={options}
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={false}
        backspaceRemovesValue={false}
        isSearchable={true}
        onChange={(value) => this._handleInternalValueChanged(name, value)}
      />
    );
  }

  _handleInternalValueChanged(name, newValueObj) {
    let newValue = getNewSelectionValue(newValueObj, true);

    this._updateValueFromState({
      ...this.state,
      [name]: newValue,
    });
  }

  _handleTimestampChanged(timestamp) {
    this.setState(
      {
        timestamp,
      },
      () => {
        this._updateValueFromState(this.state);
      },
    );
  }

  _updateValueFromState(state) {
    const { onChange, name } = this.props;
    const { interval } = state;
    const currentTimeState = this._getStateFromCurrentTime(interval);
    const hh = state.hh || currentTimeState.hh;
    const mm = state.mm || currentTimeState.mm;
    const aa = state.aa || currentTimeState.aa;
    const dayOfWeek = state.dayOfWeek || currentTimeState.dayOfWeek;
    const dayOfMonth = state.dayOfMonth || currentTimeState.dayOfMonth;
    const timestamp = state.timestamp || currentTimeState.timestamp;
    const n = state.n || currentTimeState.n;

    const newValue = UIUtils.getCron({
      interval,
      hh,
      mm,
      aa,
      dayOfWeek,
      dayOfMonth,
      timestamp,
      n,
    });

    this.setState({
      interval,
      hh,
      mm,
      aa,
      dayOfWeek,
      dayOfMonth,
      timestamp,
      n,
    });

    onChange({ name, value: newValue });
  }

  _updateStateFromProps(props) {
    const { value } = props;

    if (!value) {
      return;
    }

    const { interval, hh, mm, aa, dayOfWeek, dayOfMonth, timestamp, n } = UIUtils.parseCron(value);

    this.setState({
      interval,
      hh,
      mm,
      aa,
      dayOfWeek,
      dayOfMonth,
      timestamp,
      n,
    });
  }

  _getStateFromCurrentTime(interval) {
    return UIUtils.getCurrentDateAndTime(interval);
  }
}

FrequencyFieldRedesign.defaultProps = {
  skinnyDisplay: false,
  limitOptions: null,
  iconColor: '#ff7436',
  labelStyle: { fontWeight: 'normal' },
};

export default FrequencyFieldRedesign;
